import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { FormattedMessage } from 'react-intl';
// Store
import { IRootState } from '../../redux';
// Styles
import styles from './activityLog.module.scss';
// Components
import { Button, CustomLink, DateTimePicker, Dropdown, Image, Input, Loader } from '..';
// Providers
import { getActivityLog, getActivityLogDetails } from '../../services/businessEventlog.service';
import { getUserIdentities, lookupIndentity, lookupUserId } from '../../services/userIdentity.service';
// Assets
import images from '../../assets/images/images';
// Helpers
import { getDateTimeTemplate, getUTCString } from '../../helpers/dateTime';
import { getCurrentLanguage, sortListByCurrentLanguage } from '../../helpers/general';
import KeyboardHandler from '../../helpers/keyboardHandler';
// Enums
import { DateTimeFormat } from '../../enums';
// Interfaces
import { INonEventData, IItem } from '../../interfaces/general.interfaces';
import { IEventRecord, IEventType } from '../../interfaces/activityLogs.interfaces';

interface IActivityLogScreenState {
    hasMoreRecords: boolean;
    loadMoreItems: boolean;
    currentPage: number;
    records: Array<IEventRecord>;
    loading: boolean;
    expandMultiple: boolean;
    formValues: {
        executingUserIdentityType: string;
        affectedUserIdentityType: string;
        fromDate: string;
        toDate: string;
        eventType: string;
        executingUser: string;
        affectedUser: string;
        executingUserId: string;
        affectedUserId: string;
        description: string;
    };
}

interface IOwnProps {
    eventTypes: IEventType[];
    showUserFilter?: boolean;
}

interface IRowData {
    record: IEventRecord;
    index: number;
}

class _ActivityLogScreen extends React.Component<ReduxType, IActivityLogScreenState> {
    userTypes: IItem[] = [
        {
            key: 'USER_ID',
            value: null,
        },
    ];

    public takeCount = 20;

    public keyboardHandlerInstance = new KeyboardHandler();

    constructor(props: ReduxType) {
        super(props);
        this.state = {
            loadMoreItems: true,
            hasMoreRecords: false,
            currentPage: 0,
            loading: false,
            records: null,
            expandMultiple: false,
            formValues: {
                eventType: '',
                fromDate: null,
                description: '',
                toDate: null,
                executingUserIdentityType: null,
                affectedUserIdentityType: null,
                executingUser: '',
                affectedUser: '',
                executingUserId: this.props.showUserFilter ? '' : window.sessionStorage.getItem('userId'),
                affectedUserId: '',
            },
        };
    }

    componentDidMount() {
        this.findRecords(true);
        this.setupKeyboardHandlers();
    }

    componentDidUpdate(prevProps: ReduxType) {
        if (prevProps.languageId && prevProps.languageId !== this.props.languageId) {
            this.findRecords(true);
        }
    }

    currentShownRecordsCount = () => {
        return (this.takeCount / 2) * this.state.currentPage;
    };

    setupKeyboardHandlers = () => {
        this.keyboardHandlerInstance.addHandlers({
            enter: (event: React.KeyboardEvent, data: IRowData) => {
                this.onRecordClicked(data.record, data.index);
            },
        });
    };

    formatList = (rawRecords: Array<IEventRecord>) => {
        return rawRecords.map((rawRecord) => {
            return {
                date: rawRecord.date,
                eventType: rawRecord.eventType,
                descriptions: rawRecord.descriptions,
            };
        });
    };

    getLanguageCode = () => {
        return this.props.supportedLanguages
            .find((language) => language.id === this.props.languageId)
            .isoCode.toUpperCase();
    };

    getEventTypes = () => {
        return this.props.eventTypes
            .map(({ type }) => type)
            .slice(1)
            .join(',');
    };

    findRecordsParamsProvider = {
        startDateTime: (language: string) => {
            return this.state.formValues.fromDate
                ? getUTCString(
                      this.state.formValues.fromDate,
                      getDateTimeTemplate(DateTimeFormat.DATE_AND_TIME, language),
                      language
                  )
                : null;
        },
        endDateTime: (language: string) => {
            return this.state.formValues.toDate
                ? getUTCString(
                      this.state.formValues.toDate,
                      getDateTimeTemplate(DateTimeFormat.DATE_AND_TIME, language),
                      language
                  )
                : null;
        },
        executingUserId: () => {
            return this.props.showUserFilter ? +this.state.formValues.executingUserId || null : null;
        },
        affectedUserId: () => {
            return this.props.showUserFilter ? +this.state.formValues.affectedUserId || null : null;
        },
        eventType: () => {
            return this.state.formValues.eventType
                ? this.state.formValues.eventType === 'all'
                    ? this.getEventTypes()
                    : this.state.formValues.eventType
                : this.getEventTypes();
        },
        affectedOrganizationId: () => {
            return this.props.showUserFilter
                ? +window.sessionStorage.getItem('selectedOrganizationId')
                : +window.sessionStorage.getItem('userOrganizationId');
        },
        descriptionText: () => {
            return this.state.formValues.description || null;
        },
    };

    findRecords = (clean?: boolean) => {
        this.setState(
            (prevState) => {
                return {
                    loadMoreItems: clean ? true : prevState.loadMoreItems,
                    loading: clean,
                    records: clean ? (prevState.records ? [] : null) : prevState.records,
                    currentPage: clean ? 1 : prevState.currentPage + 1,
                };
            },
            () => {
                this.getUserIds().then(() => {
                    const language = this.getLanguageCode().toLowerCase();
                    const searchParams = {
                        startDateTime: this.findRecordsParamsProvider.startDateTime(language),
                        endDateTime: this.findRecordsParamsProvider.endDateTime(language),
                        executingUserId: this.findRecordsParamsProvider.executingUserId(),
                        affectedUserId: this.findRecordsParamsProvider.affectedUserId(),
                        eventTypes: this.findRecordsParamsProvider.eventType(),
                        languageCode: this.getLanguageCode(),
                        descriptionText: this.findRecordsParamsProvider.descriptionText(),
                        skip: this.state.records ? this.state.records.length : 0,
                        take: this.takeCount,
                        affectedOrganizationId: null as number,
                    };
                    if (this.state.loadMoreItems) {
                        getActivityLog(searchParams).then((res) => {
                            this.setState((prevState) => {
                                return {
                                    loadMoreItems: res.length >= this.takeCount,
                                    records: prevState.records ? [...prevState.records, ...res] : res,
                                    loading: false,
                                };
                            }, this.checkHasMoreItems);
                        });
                    } else {
                        this.checkHasMoreItems();
                    }
                });
            }
        );
    };

    getUserIds = () => {
        return new Promise<void>((resolve) => {
            Promise.all([
                this.getUserIdBasedOnIdentityType('executing'),
                this.getUserIdBasedOnIdentityType('affected'),
            ]).then(([executingUserId, affectedUserId]) => {
                this.setState((prevState) => {
                    return {
                        formValues: {
                            ...prevState.formValues,
                            executingUserId,
                            affectedUserId,
                        },
                    };
                }, resolve);
            });
        });
    };

    getUserIdBasedOnIdentityType = (type: string): Promise<string> => {
        if (this.state.formValues[type + 'UserIdentityType'] === null) {
            return this.getPlainUserId(type);
        } else {
            return this.getUserIdByIdentityType(type);
        }
    };

    getPlainUserId = (type: string): Promise<string> => {
        return new Promise((resolve) => {
            resolve(this.state.formValues[type + 'User']);
        });
    };

    getUserIdByIdentityType = (type: string): Promise<string> => {
        return new Promise((resolve) => {
            if (!this.state.formValues[type + 'User']) return resolve(null);
            return lookupUserId({
                organizationId: +window.sessionStorage.getItem('selectedOrganizationId'),
                identities: [
                    {
                        identity: this.state.formValues[type + 'User'],
                        identityTypeId: this.state.formValues[type + 'UserIdentityType'],
                    },
                ],
            }).then((response) => {
                resolve(String(response[0] ? response[0].userId : -1));
            });
        });
    };

    checkHasMoreItems = () => {
        this.setState((prevState) => {
            return {
                hasMoreRecords: prevState.records.length > this.currentShownRecordsCount(),
            };
        });
    };

    onRecordClicked = (record: IEventRecord, recordIndex: number) => {
        if (!this.state.expandMultiple) {
            this.hideOneRecord(recordIndex, () => {
                this.toggleRecordDetails(record, recordIndex);
            });
        } else {
            this.toggleRecordDetails(record, recordIndex);
        }
    };

    toggleRecordDetails = (record: IEventRecord, recordIndex: number) => {
        if (record.detailsShown || record.details) {
            const records = [...this.state.records];
            records[recordIndex].detailsShown = !records[recordIndex].detailsShown;
            this.setState({ records });
        } else {
            getActivityLogDetails(record.id).then((res) => {
                const records = [...this.state.records];
                records[recordIndex].detailsShown = !records[recordIndex].detailsShown;
                records[recordIndex].details = res;
                records[recordIndex].details.identity = {
                    executingUserId: null,
                    affectedUserId: null,
                };
                this.setState({ records });
            });
        }
    };

    inputChange = (event: React.FormEvent | INonEventData, isValid: boolean) => {
        const target = event.currentTarget as HTMLInputElement;
        const value = target.value;
        const name = target.name;

        this.setState((prevState) => {
            return {
                formValues: {
                    ...prevState.formValues,
                    [name]: value,
                },
            };
        });
    };

    convertUserIdToIdentity = (idType: string, recordIndex: number) => {
        const userId = this.state.records[recordIndex].details[idType];
        getUserIdentities(userId).then((res) => {
            lookupIndentity({
                userId,
                identityTypeId: res.items[0].identityTypeId,
            }).then((res) => {
                const records = [...this.state.records];
                records[recordIndex].details.identity[idType] = res[0].identity;
                this.setState({ records });
            });
        });
    };

    renderRecordDetails = (record: IEventRecord, index: number) => {
        return (
            <div className="d-flex flex-column justify-content-start pl-3">
                {record.descriptions && (
                    <div>
                        <strong className="d-inline-block text-right">
                            <FormattedMessage id="DESCRIPTION" />:
                        </strong>
                        <span className="ml-2">{record.descriptions}</span>
                    </div>
                )}
                {this.props.showUserFilter && (
                    <>
                        {record.details.executingUserId && (
                            <div className="mt-2">
                                <strong className="d-inline-block text-right">
                                    <FormattedMessage id="EXECUTING_USER_ID" />:
                                </strong>
                                {record.details.identity.executingUserId ? (
                                    <span className="ml-2">{record.details.identity.executingUserId}</span>
                                ) : (
                                    <CustomLink onClick={() => this.convertUserIdToIdentity('executingUserId', index)}>
                                        <span className="ml-2">{record.details.executingUserId}</span>
                                    </CustomLink>
                                )}
                            </div>
                        )}
                        {record.details.affectedUserId && (
                            <div className="mt-2">
                                <strong className="d-inline-block text-right">
                                    <FormattedMessage id="AFFECTED_USER_ID" />:
                                </strong>
                                {record.details.identity.affectedUserId ? (
                                    <span className="ml-2">{record.details.identity.affectedUserId}</span>
                                ) : (
                                    <CustomLink onClick={() => this.convertUserIdToIdentity('affectedUserId', index)}>
                                        <span className="ml-2">{record.details.affectedUserId}</span>
                                    </CustomLink>
                                )}
                            </div>
                        )}
                        {record.details.data &&
                            Object.keys(record.details.data).map((key, index) => {
                                return (
                                    <div key={index} className="mt-2">
                                        <strong className="d-inline-block text-right">{key}:</strong>
                                        <span className="ml-2">{record.details.data[key]}</span>
                                    </div>
                                );
                            })}
                    </>
                )}
            </div>
        );
    };

    onEventTypeSelected = (value: IEventType) => {
        const eventTypeValue = value ? (value.type === 'all' ? '' : value.type) : null;

        this.setState((prevState) => {
            return {
                formValues: {
                    ...prevState.formValues,
                    eventType: eventTypeValue,
                },
            };
        });
    };

    toggleExpandMode = () => {
        this.setState(
            (prevState) => {
                return {
                    expandMultiple: !prevState.expandMultiple,
                };
            },
            () => {
                !this.state.expandMultiple && this.hideAllRecords();
            }
        );
    };

    hideAllRecords = () => {
        const records = this.state.records.map((record) => {
            return { ...record, detailsShown: false };
        });
        this.setState({ records });
    };

    hideOneRecord = (triggeredRecordIndex: number, callback: () => void) => {
        const records = this.state.records;
        const openedRecordIndex = this.state.records.findIndex((record) => record.detailsShown);
        if (openedRecordIndex > -1 && openedRecordIndex !== triggeredRecordIndex) {
            records[openedRecordIndex].detailsShown = false;
        }
        this.setState({ records }, callback);
    };

    getTargetEntityInputValue = (name: string) => {
        return this.state.formValues[name] === null ? '' : this.state.formValues[name];
    };

    getEventTypeKey = (eventTypeValue: string) => {
        const eventType = this.props.eventTypes.find((el) => el.type === eventTypeValue);
        return eventType ? eventType.label : '';
    };

    getSortedEventTypeList = () => {
        const { eventTypes, supportedLanguages, languageId } = this.props;
        const userLanguage = getCurrentLanguage(supportedLanguages, languageId);
        const eventTypesList = eventTypes.slice(1, eventTypes.length);
        const sortedEventTypesList = sortListByCurrentLanguage(
            eventTypesList,
            (eventType) => String(eventType.label),
            userLanguage
        );

        return [eventTypes[0], ...sortedEventTypesList];
    };

    onIdentityTypeSelected = (type: string, item: IItem) => {
        const { value } = item;
        this.setState((prevState) => {
            return {
                formValues: {
                    ...prevState.formValues,
                    [type + 'UserIdentityType']: value,
                    [type + 'User']: '',
                },
            };
        });
    };

    public render() {
        const language = this.getLanguageCode().toLowerCase();
        return (
            <div className={styles.activityLogContent}>
                <div className="bg-light-grey p-4">
                    <div
                        className={`d-flex ${
                            this.props.showUserFilter ? 'flex-column mt-2' : 'flex-column flex-md-row'
                        }`}
                    >
                        {this.props.showUserFilter && (
                            <div className="d-flex flex-column flex-fill flex-shrink-0 p-0">
                                <div className="d-flex flex-column flex-md-row order-1">
                                    <div className="d-flex flex-column col-12 col-md-6 p-0 mt-2 mt-md-0">
                                        <div className="d-flex flex-column flex-md-row">
                                            <Dropdown
                                                className="col-12 col-md-4"
                                                initial={this.userTypes[0]}
                                                onSelect={(item) => this.onIdentityTypeSelected('executing', item)}
                                                list={this.userTypes}
                                                translateData
                                            />
                                            <Input
                                                name="executingUser"
                                                numeric={this.state.formValues.executingUserIdentityType === null}
                                                className="ml-md-1 mt-2 mt-md-0"
                                                value={this.getTargetEntityInputValue('executingUser')}
                                                placeholder="EXECUTING_USER"
                                                onChange={this.inputChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="d-flex flex-column col-12 col-md-6 p-0 pl-md-1 mt-2 mt-md-0">
                                        <div className="d-flex flex-column flex-md-row">
                                            <Dropdown
                                                className="col-12 col-md-4"
                                                initial={this.userTypes[0]}
                                                onSelect={(item) => this.onIdentityTypeSelected('affected', item)}
                                                list={this.userTypes}
                                                translateData
                                            />
                                            <Input
                                                name="affectedUser"
                                                numeric={this.state.formValues.affectedUserIdentityType === null}
                                                className="ml-md-1 mt-2 mt-md-0"
                                                value={this.getTargetEntityInputValue('affectedUser')}
                                                placeholder="AFFECTED_USER"
                                                onChange={this.inputChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div
                            className={`d-flex flex-column flex-md-row p-0 ${
                                this.props.showUserFilter ? 'my-2' : ''
                            } flex-fill order-3`}
                        >
                            <div
                                className={`d-flex flex-column col-12 ${
                                    this.props.showUserFilter ? 'col-md-6' : ''
                                } p-0 mt-2 mt-md-0`}
                            >
                                <Dropdown
                                    className="w-100"
                                    list={this.getSortedEventTypeList()}
                                    onSelect={this.onEventTypeSelected}
                                    initial={this.props.eventTypes[0]}
                                    placeholder="SELECT_EVENT_TYPE_LABEL"
                                    ariaLabel="SELECT_EVENT_TYPE_LABEL"
                                    displayKey="label"
                                    translateData
                                />
                            </div>
                            <div className="col-md-6 p-0 pl-md-1 mt-2 mt-md-0">
                                {this.props.showUserFilter && (
                                    <div className="flex-fill">
                                        <Input
                                            name="description"
                                            value={this.getTargetEntityInputValue('description')}
                                            placeholder="ACTIVITY_LOG_DESCRIPTION"
                                            onChange={this.inputChange}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div
                            className={`order-3 mt-2 mt-md-0 col-md-6 p-0 ${
                                !this.props.showUserFilter ? 'pl-md-1' : ''
                            }`}
                        >
                            <div className="d-flex flex-column flex-md-row">
                                <div className="w-100">
                                    <DateTimePicker
                                        name="fromDate"
                                        value={this.state.formValues.fromDate}
                                        onChange={this.inputChange}
                                        placeholder="FROM_DATE_LABEL"
                                        format={DateTimeFormat.DATE_AND_TIME}
                                        language={language}
                                    />
                                </div>
                                <div className="ml-md-1 mt-2 mt-md-0 w-100">
                                    <DateTimePicker
                                        name="toDate"
                                        value={this.state.formValues.toDate}
                                        onChange={this.inputChange}
                                        placeholder="TO_DATE_LABEL"
                                        format={DateTimeFormat.DATE_AND_TIME}
                                        language={language}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <Button onClick={() => this.findRecords(true)} className="mt-3">
                        <FormattedMessage id="SEARCH" />
                    </Button>
                </div>
                {this.state.records && this.state.records.length ? (
                    <div>
                        <div className="overflow-auto">
                            <table className={[styles.logTable, 'w-100 my-3'].join(' ')}>
                                <colgroup>
                                    <col className="w-25" />
                                    <col className="w-25" />
                                    <col className="w-50" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>
                                            <FormattedMessage id="DATE" />
                                        </th>
                                        <th>
                                            <FormattedMessage id="ACTIVITY_TYPE" />
                                        </th>
                                        <th className="d-flex justify-content-between">
                                            <FormattedMessage id="DESCRIPTION" />
                                            <div>
                                                {this.props.showUserFilter && (
                                                    <>
                                                        <FormattedMessage id="EXPAND" />
                                                        <CustomLink className="ml-2" onClick={this.toggleExpandMode}>
                                                            {this.state.expandMultiple ? (
                                                                <FormattedMessage id="MANY" />
                                                            ) : (
                                                                <FormattedMessage id="ONE" />
                                                            )}
                                                        </CustomLink>
                                                    </>
                                                )}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.records
                                        .slice(0, this.currentShownRecordsCount())
                                        .map((record, index) => {
                                            return (
                                                <Fragment key={index}>
                                                    <tr
                                                        className={styles.recordRow}
                                                        onKeyPress={(e) =>
                                                            this.keyboardHandlerInstance.onPress(e, {
                                                                record,
                                                                index,
                                                            })
                                                        }
                                                        onClick={() => this.onRecordClicked(record, index)}
                                                        tabIndex={0}
                                                    >
                                                        <td className="text-truncate">
                                                            <time>
                                                                {getUTCString(
                                                                    record.date,
                                                                    getDateTimeTemplate(
                                                                        DateTimeFormat.FULL_DATE_AND_TIME_NUMERIC,
                                                                        language
                                                                    ),
                                                                    language
                                                                )}
                                                            </time>
                                                        </td>
                                                        <td
                                                            className="text-truncate"
                                                            title={this.props.intl.formatMessage({
                                                                id: this.getEventTypeKey(record.eventType),
                                                            })}
                                                        >
                                                            <FormattedMessage
                                                                id={this.getEventTypeKey(record.eventType)}
                                                            />
                                                        </td>
                                                        <td
                                                            className={[
                                                                'd-flex justify-content-between',
                                                                styles.description,
                                                            ].join(' ')}
                                                            title={record.descriptions}
                                                        >
                                                            <div className="text-truncate pr-3">
                                                                {record.descriptions}
                                                            </div>
                                                            {record.detailsShown ? (
                                                                <Image
                                                                    style={{ height: 16 }}
                                                                    src={images.chevronUp}
                                                                    alt="HIDE"
                                                                />
                                                            ) : (
                                                                <Image
                                                                    style={{ height: 16 }}
                                                                    src={images.chevronDown}
                                                                    alt="EXPAND"
                                                                />
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {record.detailsShown && (
                                                        <tr className={[styles.details].join(' ')}>
                                                            <td colSpan={4}>
                                                                {this.renderRecordDetails(record, index)}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Fragment>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                        <div className="d-flex justify-content-center p-2">
                            {this.state.hasMoreRecords && (
                                <Button
                                    onClick={() => {
                                        this.findRecords();
                                    }}
                                >
                                    <FormattedMessage id="LOAD_MORE" />
                                </Button>
                            )}
                        </div>
                    </div>
                ) : null}
                {this.state.records && !this.state.records.length && !this.state.loading && (
                    <div className="my-5">
                        <FormattedMessage id="NO_RECORDS" />
                    </div>
                )}
                {this.state.loading && (
                    <div className="my-5 d-flex align-items-center justify-content-center">
                        <Loader show size="lg" />
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = ({ user, appReadouts }: IRootState) => {
    return {
        languageId: user.languageId,
        organizationId: user.organizationId,
        supportedLanguages: appReadouts.supportedLanguages,
    };
};

type ReduxType = ReturnType<typeof mapStateToProps> & WrappedComponentProps & IOwnProps;

export const ActivityLog = connect(mapStateToProps)(injectIntl(_ActivityLogScreen));
